import { _lang } from '@umu/ukit-lang';
import { lang } from '@umu/ukit-lang';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { components } from 'common';
const { If, Loading } = components;
import { useClickOutside } from '@umu/umu-react-hooks';
import './mediaDeviceSelector.scss';

import resetImgCN from './images/reset-permission.png';
import resetImgJP from './images/reset-permission-jp.png';
import resetImgEN from './images/reset-permission-en.png';
import resetImgTW from './images/reset-permission-tw.png';
import resetImgTH from './images/reset-permission-th.png';
import resetImgES from './images/reset-permission-es.png';

let resetPermissionImg = resetImgEN;
if (_lang('CN')) {
    resetPermissionImg = resetImgCN;
}
if (_lang('JP')) {
    resetPermissionImg = resetImgJP;
}
if (_lang('TW')) {
    resetPermissionImg = resetImgTW;
}
if (_lang('TH')) {
    resetPermissionImg = resetImgTH;
}
if (_lang('ES')) {
    resetPermissionImg = resetImgES;
}

enum EDeviceStatus {
    PROMPT = 'prompt',
    GRANTED = 'granted',
    DENIED = 'denied',
}

const isOperationSystemWindows = navigator.appVersion.indexOf('Win') !== -1;

interface IPermissionStatus {
    onchange: () => void;
    state: EDeviceStatus;
}

// | FIREFOX 56	           | CHROME 62                     |            错误处理                            |
// | --------------------- | ----------------------------- | --------------------------------------------  |
// | NotFoundError         |	DevicesNotFoundError       |              设备没有找到，如何处理               |
// | NotReadableError      |	TrackStartError            |            获取其他设备信息，获取不到             |
// | OverconstrainedError  |	ConstraintNotSatisfiedError|               请求的视频、音频格式不支持          |
// | NotAllowedError	   |	PermissionDeniedError      |               权限不足的问题                    |

export enum EPermissionError {
    NO_ERROR = 'NoError',
    NOT_FOUND_ERROR = 'NotFoundError',
    NOT_READABLE_ERROR = 'NotReadableError',
    NOT_ALLOWED_ERROR = 'NotAllowedError',
}

export interface IPermissionError {
    name: EPermissionError;
    message: string;
}

export interface IDeviceSelectorProps {
    permissionError: IPermissionError;
    onStartRecord: (audioId: string, videoId: string) => void;
}

export interface ISelectedDevice {
    audio: string;
    video: string;
}

async function requestUserMediaPermission({
    audioDeviceId,
    videoDeviceId,
}: {
    audioDeviceId?: string;
    videoDeviceId?: string;
}) {
    return new Promise(async (resolve, reject) => {
        try {
            const params = {
                audio: { deviceId: audioDeviceId || 'default' },
                video: { deviceId: videoDeviceId || 'default' },
            };

            const stream = await navigator.mediaDevices.getUserMedia(params);
            resolve(stream);

            // close media stream after 100ms
            setTimeout(() => {
                if (stream) {
                    stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
            }, 100);
        } catch (err) {
            reject(err);
        }
    });
}

export function MediaDeviceSelector({ permissionError, onStartRecord }: IDeviceSelectorProps) {
    const [permissionErr, setPermissionErr] = useState<IPermissionError>(permissionError);
    const [selectedDevices, setSelectedDevices] = useState<ISelectedDevice>({ video: '', audio: '' });
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);

    const [videoStatus, setVideoStatus] = useState<EDeviceStatus>(EDeviceStatus.PROMPT);
    const [audioStatus, setAudioStatus] = useState<EDeviceStatus>(EDeviceStatus.PROMPT);
    const [isReloadVideoPermission, setIsReloadVideoPermission] = useState<boolean>(false);

    useEffect(() => {
        requestPermissionStatus();
    }, []);

    useEffect(() => {
        setPermissionErr(permissionError);
    }, [permissionError]);

    const enumMediaDevices = async () => {
        try {
            const result = await navigator.mediaDevices.enumerateDevices();
            const inputDevices = result.filter((item: MediaDeviceInfo) => {
                return item.kind.endsWith('input') && item.deviceId !== '';
            });
            const audioInputs = inputDevices.filter(
                (item: MediaDeviceInfo) => item.kind === 'audioinput',
            ) as MediaDeviceInfo[];

            setAudioDevices(audioInputs);
            let audioId = '';
            if (audioInputs && audioInputs.length > 0) {
                audioId = audioInputs[0].deviceId;
            }

            // set video input devices
            const videoInputs = inputDevices.filter(
                (item: MediaDeviceInfo) => item.kind === 'videoinput',
            ) as MediaDeviceInfo[];
            setVideoDevices(videoInputs);
            let videoId = '';
            if (videoInputs && videoInputs.length > 0) {
                videoId = videoInputs[0].deviceId;
            }

            setSelectedDevices({ video: videoId, audio: audioId });
        } catch (error) {
            console.log('Error accessing media devices:', error);
        }
    };

    const setPermissionStatus = (result: IPermissionStatus) => {
        const newStatus = result.state;
        if (newStatus === EDeviceStatus.GRANTED) {
            setPermissionErr({ name: EPermissionError.NO_ERROR, message: '' });
        } else if (newStatus === EDeviceStatus.DENIED) {
            setPermissionErr({ name: EPermissionError.NOT_ALLOWED_ERROR, message: 'Permission denied' });
        } else if (newStatus === EDeviceStatus.PROMPT) {
            requestUserMediaPermission({});
        }
    };

    const requestPermissionStatusBase = async (name: string) => {
        try {
            const result = await (navigator as any).permissions.query({ name: name });
            return result;
        } catch (error) {
            return {
                name,
                state: 'granted',
                onchange: () => {},
            };
        }
    };

    const requestPermissionStatus = async () => {
        Promise.all([requestPermissionStatusBase('camera'), requestPermissionStatusBase('microphone')]).then(
            ([videoResult, audioResult]: [IPermissionStatus, IPermissionStatus]) => {
                setVideoStatus(videoResult.state);
                setAudioStatus(audioResult.state);

                enumMediaDevices();

                videoResult.onchange = () => {
                    setVideoStatus(videoResult.state);
                    enumMediaDevices();
                    setPermissionStatus(videoResult);
                };

                audioResult.onchange = () => {
                    setAudioStatus(audioResult.state);
                    enumMediaDevices();
                    setPermissionStatus(audioResult);
                };
            },
        );
    };

    const isNotAllowed = permissionErr.name === EPermissionError.NOT_ALLOWED_ERROR;
    const btnDisabled =
        isNotAllowed ||
        permissionErr.name === EPermissionError.NOT_FOUND_ERROR ||
        permissionErr.name === EPermissionError.NOT_READABLE_ERROR;

    const onRecord = useCallback(() => {
        if (btnDisabled) {
            return;
        }
        onStartRecord(selectedDevices.audio, selectedDevices.video);
    }, [selectedDevices, btnDisabled]);

    const onAudioDeviceChange = useCallback(
        (audioId: string) => {
            setSelectedDevices({ audio: audioId, video: selectedDevices.video });
        },
        [selectedDevices],
    );

    const onVideoDeviceChange = useCallback(
        async (videoId: string) => {
            // windows操作系统下，多个进程无法共享同一个摄像头设备，这里在切换摄像头设备的时候重新获取是否可用状态
            if (isOperationSystemWindows) {
                // 1. request permission
                try {
                    setIsReloadVideoPermission(true);
                    await requestUserMediaPermission({ videoDeviceId: videoId });
                    setIsReloadVideoPermission(false);
                    setPermissionErr({ name: EPermissionError.NO_ERROR, message: '' });
                } catch (err) {
                    setIsReloadVideoPermission(false);
                    // @ts-ignore
                    setPermissionErr({ name: err.name, message: err.message });
                }
            }
            setSelectedDevices({ audio: selectedDevices.audio, video: videoId });
        },
        [selectedDevices],
    );

    return (
        <React.Fragment>
            {isReloadVideoPermission && <Loading className="change-video-loading" />}
            {isNotAllowed ? (
                <img className="reset-permission-img" src={resetPermissionImg} alt="reset-permission-img" />
            ) : (
                <>
                    <DeviceSelector
                        type="audio"
                        deviceList={audioDevices}
                        selectedId={selectedDevices.audio}
                        status={audioStatus}
                        errorName={permissionErr.name}
                        onChange={onAudioDeviceChange}
                    />
                    <DeviceSelector
                        type="video"
                        deviceList={videoDevices}
                        selectedId={selectedDevices.video}
                        status={videoStatus}
                        errorName={permissionErr.name}
                        onChange={onVideoDeviceChange}
                    />
                </>
            )}

            <button className={`video-record-btn ${btnDisabled ? ' disabled' : ''}`} onClick={onRecord}>
                {lang('开始录制')}
            </button>
        </React.Fragment>
    );
}

interface IDeviceSelectorItemProps {
    type: string;
    status: string;
    errorName?: EPermissionError;
    deviceList: MediaDeviceInfo[];
    selectedId: string;
    onChange: (deviceId: string) => void;
}

const ERROR_HINT_MAP: Record<string, (name: string) => string> = {
    [EPermissionError.NOT_READABLE_ERROR]: (name: string) => lang('检测到{$0}被占用，请关闭其他应用程序后再试。', name),
    [EPermissionError.NOT_FOUND_ERROR]: (name: string) => lang('没有检测到可用{$0}。', name),
};

function DeviceSelector({ type, deviceList, selectedId, errorName, onChange }: IDeviceSelectorItemProps) {
    const listWrapRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    useClickOutside(listWrapRef, () => setIsOpen(false));

    const onClick = useCallback(() => {
        if (deviceList.length > 0) {
            setIsOpen(!isOpen);
        }
    }, [isOpen, deviceList]);

    const handleDeviceChange = useCallback(
        (deviceId) => {
            setIsOpen(false);
            onChange(deviceId);
        },
        [onChange],
    );

    const selectedDevice = deviceList.find((item: MediaDeviceInfo) => item.deviceId === selectedId);
    const deviceName = selectedDevice ? selectedDevice.label : '--';
    let errorMsg = '';
    if (
        (errorName === EPermissionError.NOT_FOUND_ERROR && deviceList.length === 0) ||
        errorName === EPermissionError.NOT_READABLE_ERROR
    ) {
        const deviceTypeName = type === 'video' ? lang('摄像头') : lang('麦克风');
        if (errorName) {
            errorMsg = ERROR_HINT_MAP[errorName](deviceTypeName);
        } else {
            errorMsg = '';
        }
        // audio流可以进行多个进程之间共享
        // 如果是无法多进程占用的错误，不会在audio中显示
        if (errorName === EPermissionError.NOT_READABLE_ERROR && type === 'audio') {
            errorMsg = '';
        }
    }

    return (
        <div ref={listWrapRef} className="device-selector-wrap">
            <div className="device-selector">
                <i className={`type-icon ${type}`}></i>
                <div className="device-container">
                    <div className="device-name" onClick={onClick}>
                        <span>{deviceName}</span>
                        {deviceList.length > 0 && <i className={`icon-arrow ${isOpen ? 'down' : 'up'}`}></i>}
                    </div>
                    <If condition={isOpen && deviceList.length > 0}>
                        <DeviceList deviceList={deviceList} selectedId={selectedId} onChange={handleDeviceChange} />
                    </If>
                </div>
            </div>
            {errorMsg && <span className="error">{errorMsg}</span>}
        </div>
    );
}

interface IDeviceListProps {
    deviceList: MediaDeviceInfo[];
    selectedId: string;
    onChange: (deviceId: string) => void;
}

function DeviceList({ deviceList, selectedId, onChange }: IDeviceListProps) {
    const onClickItem = useCallback(
        (deviceId: string) => {
            onChange(deviceId);
        },
        [selectedId, onChange],
    );

    return (
        <div className="device-list">
            {deviceList.map((item: MediaDeviceInfo) => (
                <div
                    key={item.deviceId}
                    className={`device-item ${selectedId === item.deviceId ? 'selected' : ''}`}
                    onClick={() => onClickItem(item.deviceId)}
                >
                    <i className="icon" />
                    <p>{item.label}</p>
                </div>
            ))}
        </div>
    );
}
